/**
 * One POST /api/products/batch-refresh-live per <=100 SKUs instead of one
 * GET /api/products/search per SKU (perf audit 2026-09-29: products/search
 * ran 101×/hour at p50 2.2 s, mostly from per-SKU Promise.all loops).
 *
 * Endpoint contract (backend/server.js): body { skus: string[] } (server
 * truncates to 100), response { updates: [{ sku, stockOnHand, stockAvailable,
 * stockCommitted, weight, weightUnit, barcode, binLocation, overflow1,
 * overflow2, length, width, height, inventoryLevels, imageUrl, updated: true }],
 * notFound: string[], duration }. Each SKU resolves to its FIRST store match.
 *
 * imageUrl rides along since 2026-10-29: without it a card's thumbnail was
 * frozen at whatever it had when first added (localStorage rows showed photos
 * 12+ days out of date after Shopify images were replaced).
 */

import { sameBarcodeList } from './productBarcodes';

export const LIVE_STOCK_BATCH_LIMIT = 100;

const skuKey = (sku) => String(sku || '').trim().toUpperCase();

/**
 * @returns {Promise<Map<string, object>>} upper-cased SKU -> live update.
 * Chunk failures are logged and skipped; callers keep their cached values.
 */
export async function fetchLiveStockBatch(http, apiUrl, skus, { signal } = {}) {
  const unique = [...new Set((skus || []).map((sku) => String(sku || '').trim()).filter(Boolean))];
  const bySku = new Map();
  const chunks = [];
  for (let i = 0; i < unique.length; i += LIVE_STOCK_BATCH_LIMIT) {
    chunks.push(unique.slice(i, i + LIVE_STOCK_BATCH_LIMIT));
  }
  await Promise.all(chunks.map(async (chunk) => {
    try {
      const response = await http.post(`${apiUrl}/api/products/batch-refresh-live`, { skus: chunk }, signal ? { signal } : undefined);
      (response?.data?.updates || []).forEach((update) => {
        if (update?.sku) bySku.set(skuKey(update.sku), update);
      });
    } catch (error) {
      if (error?.name === 'CanceledError') throw error;
      console.warn(`⚠️ [LIVE-BATCH] Refresh failed for ${chunk.length} SKU(s):`, error?.message);
    }
  }));
  return bySku;
}

export function getLiveStockUpdate(bySku, sku) {
  return bySku?.get(skuKey(sku)) || null;
}

const pick = (value, fallback) => (value !== undefined && value !== null ? value : fallback);

/**
 * Overlay a batch-refresh-live update on a product row. Fields the endpoint
 * does not return (title, price, dbBinLocation, noBarcodeAck, ...) keep the
 * row's value. imageUrl is taken when the live row has one, so a replaced
 * Shopify photo reaches cards already on the grid.
 */
export function mergeLiveStockUpdate(product, update, { isPlaceholderBarcode } = {}) {
  if (!product || !update) return product;
  const barcode = update.barcode !== undefined && update.barcode !== null
    ? update.barcode
    : (isPlaceholderBarcode && isPlaceholderBarcode(product.barcode) ? '' : product.barcode);
  return {
    ...product,
    stockOnHand: pick(update.stockOnHand, product.stockOnHand),
    stockAvailable: pick(update.stockAvailable, product.stockAvailable),
    stockCommitted: pick(update.stockCommitted, product.stockCommitted),
    weight: update.weight || product.weight,
    weightUnit: update.weightUnit || product.weightUnit,
    barcode,
    // Typed list (APP-06) only when the live read sent a different one; the
    // same list keeps its reference so isLiveStockMergeNoop still bails out.
    barcodes: Array.isArray(update.barcodes) && !sameBarcodeList(update.barcodes, product.barcodes || []) ? update.barcodes : product.barcodes,
    binLocation: update.binLocation || product.binLocation,
    overflow1: pick(update.overflow1, product.overflow1),
    overflow2: pick(update.overflow2, product.overflow2),
    length: pick(update.length, product.length),
    width: pick(update.width, product.width),
    height: pick(update.height, product.height),
    inventoryLevels: Array.isArray(update.inventoryLevels) && update.inventoryLevels.length > 0
      ? update.inventoryLevels
      : product.inventoryLevels,
    imageUrl: update.imageUrl || product.imageUrl,
    fromCache: false
  };
}

const LIVE_FIELDS = ['stockOnHand', 'stockAvailable', 'stockCommitted', 'weight', 'weightUnit', 'barcode', 'barcodes',
  'binLocation', 'overflow1', 'overflow2', 'length', 'width', 'height', 'inventoryLevels', 'imageUrl', 'fromCache'];

/** True when merging changed nothing (lets setState bail out without a re-render). */
export function isLiveStockMergeNoop(before, after) {
  return LIVE_FIELDS.every((field) => before?.[field] === after?.[field]);
}

/**
 * Apply a fetchLiveStockBatch result to a product list. The endpoint answers
 * with each SKU's FIRST store match and no storeDisplayName, so a SKU listed
 * once per store (multi-store rows) is left untouched rather than stamping one
 * store's stock onto another. Returns the SAME array when nothing changed.
 */
export function applyLiveStockUpdates(products, bySku, options = {}) {
  if (!Array.isArray(products) || !bySku || bySku.size === 0) return products;
  const rowsPerSku = new Map();
  products.forEach((p) => {
    const key = skuKey(p?.sku);
    if (key) rowsPerSku.set(key, (rowsPerSku.get(key) || 0) + 1);
  });
  let changed = false;
  const next = products.map((p) => {
    const key = skuKey(p?.sku);
    if (!key || rowsPerSku.get(key) !== 1) return p;
    const update = bySku.get(key);
    if (!update) return p;
    const merged = mergeLiveStockUpdate(p, update, options);
    if (isLiveStockMergeNoop(p, merged)) return p;
    changed = true;
    return merged;
  });
  return changed ? next : products;
}
